/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import useError from "../../hooks/useError";
import fetchHelper from "../../services/fetchHelper";
import MissedAndDueTable from "./MissedAndDueTable";
import { deviceType } from "../../utils/helpers";
import "./AllReports.css";
import { missedDueHeaderCsv } from "../../constants/HeaderCsvConstants";
import Pagination from "../../components/Pagination";
import { eventNotifierService } from "./eventNotifier";

export const Loading = ({ length = 10, colspan = 17 }) => {
  const array = new Array(length);
  array.fill(1);
  return array.map((item, index) => (
    <tbody key={index}>
      <tr>
        <td colSpan={colspan}></td>
      </tr>
      <tr>
        <td colSpan={colspan} style={{ background: "#fff", padding: 0 }}>
          <img src="data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTUwNSA0NSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KLnJlY3QgewpmaWxsOiB1cmwoI2xpbmVhci1ncmFkaWVudCk7Cn0KPC9zdHlsZT4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0ibGluZWFyLWdyYWRpZW50Ij4KPHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iI0Y3RjdGNyIgLz4KPHN0b3Agb2Zmc2V0PSIzMyUiIHN0b3AtY29sb3I9IiNGN0Y3RjciIC8+CjxzdG9wIG9mZnNldD0iNTAlIiBzdG9wLWNvbG9yPSIjZWZmMGYwIiAvPgo8c3RvcCBvZmZzZXQ9IjY3JSIgc3RvcC1jb2xvcj0iI0Y3RjdGNyIgLz4KPHN0b3Agb2Zmc2V0PSIxMDAlIiBzdG9wLWNvbG9yPSIjRjdGN0Y3IiAvPgo8YW5pbWF0ZVRyYW5zZm9ybSBhdHRyaWJ1dGVOYW1lPSJncmFkaWVudFRyYW5zZm9ybSIKdHlwZT0idHJhbnNsYXRlIgpmcm9tPSItMSAwIgp0bz0iMSAwIgpiZWdpbj0iMHMiCmR1cj0iMS41cyIKcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiLz4KPC9saW5lYXJHcmFkaWVudD4KPC9kZWZzPgoKPHJlY3QgY2xhc3M9InJlY3QiIHg9IjAuOTgyOTEiIHk9IjAuMTY3NDgiIHdpZHRoPSIxNTAzLjE1IiBoZWlnaHQ9IjQ0LjgzMjUiIHJ4PSI2IiBmaWxsPSIjZWZmMGYwIi8+CjxyZWN0IGNsYXNzPSJyZWN0IiB4PSIwLjk4MjkxIiB5PSIwLjE2NzQ4IiB3aWR0aD0iMTUwMy4xNSIgaGVpZ2h0PSI0NC44MzI1IiByeD0iNiIgZmlsbD0idXJsKCNwYWludDBfbGluZWFyKSIvPgo8L3N2Zz4=" />
        </td>
      </tr>
    </tbody>
  ));
};

const AllReports = ({ activeTab }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const showDefaultApiError = useError();
  const [offset, setOffset] = useState(0);
  const [to, setTo] = useState(null);
  const [limit, setLimit] = useState(30);
  const [initOffest, setInitOffset] = useState(true);

  const endpoint = {
    missed: `/patientmanagement/rest/interrogations/missed`,
    dues: `/patientmanagement/rest/interrogations/dues?&dueRange=14`,
  };

  useEffect(() => {
    eventNotifierService.sortEvent({});
    setInitOffset(true);
    localStorage.removeItem("patients-offset");
    activeTab in endpoint && fetchData();
  }, [activeTab]);

  useEffect(() => {
    if (!initOffest) {
      setIsLoading(true);
      localStorage.setItem("patients-offset", offset);
      if (Object.getOwnPropertyNames(eventNotifierService.getMessageValue().text).length > 0) {
        eventNotifierService.reSend();
      } else {
        fetchData();
      }
    }
  }, [offset]);

  useEffect(() => {
    eventNotifierService.getMessage().subscribe((message) => {
      if (message.text != null && Object.getOwnPropertyNames(message.text).length > 0) {
        let columnName = message.text.columnName;
        let columnValue = message.text.columnValue;
        let activeTab = message.text.activeTab;
        fetchData(columnName, columnValue, activeTab);
      }
    });
  }, []);

  const fetchData = async (sortColumn, sortValue, tabValue) => {
    const urlParams = {};

    if (sortColumn == "status") {
      urlParams.status = sortValue;
    } else if (sortColumn == "name") {
      urlParams.name = sortValue;
    } else if (sortColumn == "manufacturer") {
      urlParams.manufacturer = sortValue;
    }

    try {
      setIsLoading(true);
      const offset = Number(localStorage.getItem(`patients-offset`));
      let urlSearch = new URLSearchParams(urlParams);
      setOffset(offset);
      let url = "";
      if (tabValue) {
        url = endpoint[tabValue];
      } else {
        url = endpoint[activeTab];
      }
      if (Object.getOwnPropertyNames(urlParams).length > 0) {
        if (tabValue === "dues") {
          url = url + "&" + urlSearch;
        } else {
          url = url + "?" + urlSearch;
        }
      }
      const data = await fetchHelper.get({
        url: url,
        headers: {
          "X-Range": `items=${offset}-${offset + limit - 1}`,
          Range: `items=${offset}-${offset + (limit - 1)}`,
        },
      });
      setData(data);
      setTo(offset + data.length - 1);
      setInitOffset(false);
    } catch (err) {
      showDefaultApiError(err);
      setData([]);
      console.log(err);
    } finally {
      setIsLoading(false);
      setInitOffset(false);
    }
  };

  const missedDueCsv = data.map((item) => ({
    status:
      `${activeTab === "missed" ? "Missed" : "Due"} on: ${new Date(item?.missedDate).toLocaleDateString()}` ||
      "No data",
    name: `${item?.lastName}, ${item?.firstName}`,
    device: item?.deviceMFR || "No data",
    deviceType: deviceType(item?.deviceType) || "No data",
    billing: item?.billingCycle === "THIRTYDAY" ? "30 Days" : "90 Days",
    phone: item?.phoneNumber || "No data",
    note: item?.note,
    daysLeft: item?.daysRemaining,
  }));

  return (
    <>
      {!isLoading ? (
        <div className="csv-div-missed-due">
          <CSVLink
            data={missedDueCsv}
            headers={missedDueHeaderCsv}
            filename={activeTab + "Report.csv"}
            className="csv-link-missed-due"
          >
            <button className="btn btnTeal hideAdv">Download CSV</button>
          </CSVLink>
        </div>
      ) : null}

      <MissedAndDueTable data={data} initialLoad={isLoading} activeTab={activeTab} />
      <Pagination offset={offset} to={to} limit={limit} onSetOffset={setOffset} loading={isLoading} />
    </>
  );
};

export default AllReports;
